import * as React from "react";
import { useState, useEffect, useContext } from "react";
import {
  Button,
  Modal,
  Steps,
  Input,
  Form,
  Result,
  Space,
  Select,
  InputNumber,
  Divider,
} from "antd";
import { useStepsForm } from "sunflower-antd";
import { PlusOutlined } from "@ant-design/icons";
import type { SelectProps } from "antd";

import { AuthContext } from "../../../context/AuthContext";

import { getPlansApi } from "../../../services/Product/ProductApis";
import { getCompanyAddonsApi } from "../../../services/Addon/AddonApis";
import { getGroupApi } from "../../../services/Group/GroupApis";

const { Step } = Steps;

interface IModalProps {
  show: boolean;
  product: any;
  showModal: Function;
  groupId: any;
  handleUpdate: Function;
}

const EditProductModal: React.FC<IModalProps> = ({
  show,
  product,
  showModal,
  groupId,
  handleUpdate,
}) => {
  const { currentGroup } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [planCategory, setPlanCategory] = useState<any>(
    typeof product.PlanCategory === "object"
      ? product.PlanCategory?.value
      : product.PlanCategory ?? product.PlanCategoryID,
  );
  const [authorized, setAuthorized] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [addonOptions, setAddonOptions] = useState<SelectProps["options"]>([]);
  const [planOptions, setPlanOptions] = useState<SelectProps["options"]>([]);

  useEffect(() => {
    fetchAddons();
    fetchPlans();

    getGroupApi(String(currentGroup))
      .then((res) => {
        if (res.data.GroupLevel === 1) setAuthorized(true);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [product, currentGroup]);

  const handleModal = () => {
    showModal();
    gotoStep(0);
    form.resetFields();
  };

  const fetchAddons = async () => {
    const { data } = await getCompanyAddonsApi(groupId);

    setAddonOptions(
      data.map((addon: any) => ({
        value: addon.id,
        label: addon.Name,
      })),
    );
  };

  const fetchPlans = async () => {
    const { data } = await getPlansApi();

    setPlanOptions(
      data.map((plan: any) => ({
        value: plan.id,
        label: plan.Name,
      })),
    );
  };

  const {
    form,
    current,
    gotoStep,
    stepsProps,
    formProps,
    submit,
    formLoading,
  } = useStepsForm({
    isBackValidate: false,
    defaultFormValues: {
      ...product,
      MinAge: product.MinAge ?? product.ProductCategory?.MinAge,
      MaxAge: product.MaxAge ?? product.ProductCategory?.MaxAge,
      PlanCategory:
        typeof product.PlanCategory === "object"
          ? product.PlanCategory?.value ?? product.PlanCategory?.id
          : product.PlanCategory ?? product.PlanCategoryID,
      FamilyCovers: product.FamilyCover || product.FamilyCovers || [],
      Addons:
        product.Addons?.length
          ? product.Addons
          : product.Addon?.map((a: any) => a.id) ?? [],
    },
    async submit(values: any) {
      setLoading(true);
      const addons = (values.Addons || []).map((addon: any) =>
        typeof addon === "object" ? addon.id ?? addon.value : addon,
      );

      const familyCovers = (values.FamilyCovers || []).map((cover: any) => ({
        id: cover.id,
        MinAge: cover.MinAge,
        MaxAge: cover.MaxAge,
        CoverAmount: cover.CoverAmount,
      }));

      const planCategoryId =
        typeof values.PlanCategory === "number"
          ? values.PlanCategory
          : values.PlanCategory?.value ?? product.PlanCategoryID;

      const response = await handleUpdate(
        product.id,
        product.PlanCategoryID ?? planCategoryId,
        product.ProductCategoryID,
        {
          ProductName: values.ProductName,
          CoverAmount: values.CoverAmount,
          SpouseCoverAmount: values.SpouseCoverAmount,
          MinAge: values.MinAge,
          MaxAge: values.MaxAge,
          PlanCategory: planCategoryId,
          UnderwriterPremium: values.UnderwriterPremium,
          GroupPremium: values.GroupPremium,
          PolicyPremium: values.PolicyPremium,
          Commission: values.Commission,
          CommissionTwo: values.CommissionTwo,
          Addons: addons,
          FamilyCovers: familyCovers,
        },
      );

      if (response?.success) {
        setLoading(false);
        return true;
      }
      setErrorMsg(response?.message || "Failed to update product");
      setLoading(false);
      return false;
    },
    total: 4,
  });

  // Populate fields whenever the modal opens with product data
  useEffect(() => {
    if (show && product?.id) {
      const planCategoryValue =
        typeof product.PlanCategory === "object"
          ? product.PlanCategory?.value ?? product.PlanCategory?.id
          : product.PlanCategory ?? product.PlanCategoryID;

      const addonIds =
        product.Addons?.length
          ? product.Addons
          : product.Addon?.map((a: any) => a.id) ?? [];

      setPlanCategory(planCategoryValue);
      form.setFieldsValue({
        ...product,
        MinAge: product.MinAge ?? product.ProductCategory?.MinAge,
        MaxAge: product.MaxAge ?? product.ProductCategory?.MaxAge,
        PlanCategory: planCategoryValue,
        FamilyCovers: product.FamilyCover || product.FamilyCovers || [],
        Addons: addonIds,
      });
    }
  }, [show, product, form]);

  // Step Styling
  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 15 },
    labelAlign: "right" as const,
  };

  const tailLayout = {
    labelCol: { span: 8 },
    wrapperCol: { offset: 8, span: 15 },
  };

  const formList = [
    <>
      <Form.Item
        name="ProductName"
        label="Product Name"
        rules={[{ required: true }]}
      >
        <Input size="large" placeholder="Product Name" />
      </Form.Item>
      <Form.Item name="MinAge" label="Minimum Age" rules={[{ required: true }]}>
        <InputNumber
          style={{ width: "100%" }}
          size="large"
          placeholder="Min Age"
        />
      </Form.Item>
      <Form.Item name="MaxAge" label="Maximum Age" rules={[{ required: true }]}>
        <InputNumber
          style={{ width: "100%" }}
          size="large"
          placeholder="Max Age"
        />
      </Form.Item>
      <Form.Item
        name="PlanCategory"
        label="Plan Category"
        rules={[{ required: true }]}
      >
        <Select
          style={{ width: "100%" }}
          size="large"
          placeholder="Plan Category"
          options={planOptions}
          onChange={(value) => setPlanCategory(value)}
        />
      </Form.Item>

      {authorized && (
        <Form.Item
          name="UnderwriterPremium"
          label="Underwriter Premium"
          rules={[
            { required: true },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const groupPremiumValue = getFieldValue("GroupPremium");
                const policyPremiumValue = getFieldValue("PolicyPremium");
                if (
                  groupPremiumValue === undefined &&
                  policyPremiumValue === undefined
                ) {
                  return Promise.resolve();
                }
                if (
                  Number(value) < Number(groupPremiumValue) &&
                  Number(value) < Number(policyPremiumValue)
                ) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(
                    "Underwriter Premium should be less than Group Premium and Policy Premium",
                  ),
                );
              },
            }),
          ]}
          dependencies={["PolicyPremium", "GroupPremium"]}
        >
          <Input size="large" placeholder="Underwriter Premium" />
        </Form.Item>
      )}

      <Form.Item
        name="GroupPremium"
        label="Group Premium"
        rules={[
          { required: true },
          ({ getFieldValue }) => ({
            validator(_, value) {
              const policyPremiumValue = getFieldValue("PolicyPremium");
              if (policyPremiumValue === undefined) {
                return Promise.resolve();
              }
              if (Number(value) < Number(policyPremiumValue)) {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error("Group Premium should be less than Policy Premium"),
              );
            },
          }),
        ]}
      >
        <Input size="large" placeholder="Group Premium" />
      </Form.Item>
      <Form.Item
        name="PolicyPremium"
        label="Policy Premium"
        rules={[{ required: true }]}
      >
        <Input size="large" placeholder="Policy Premium" />
      </Form.Item>

      <Form.Item name="Commission" label="Commission One">
        <Input size="large" placeholder="Commission" />
      </Form.Item>

      <Form.Item name="CommissionTwo" label="Commission Two">
        <Input size="large" placeholder="Commission Two" />
      </Form.Item>
      <Form.Item {...tailLayout}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Button block size="large" onClick={() => gotoStep(current + 1)}>
            Next
          </Button>
        </Space>
      </Form.Item>
    </>,
    <>
      <Form.Item
        name="CoverAmount"
        label="Main Cover Amount"
        rules={[{ required: true }]}
      >
        <Input size="large" placeholder="Main Cover Amount" />
      </Form.Item>

      {planCategory === 4 && (
        <Form.Item
          name="SpouseCoverAmount"
          label="Spouse Cover Amount"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input size="large" placeholder="Spouse Cover Amount" />
        </Form.Item>
      )}

      <Form.Item name="Addons" label="Addons">
        <Select
          size="large"
          mode="multiple"
          placeholder="Product Addons"
          style={{ width: "100%" }}
          options={addonOptions}
        />
      </Form.Item>

      {planCategory === 4 && (
        <Form.List name="FamilyCovers">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }, index) => (
                <div key={key} style={{ marginBottom: 24 }}>
                  <Divider orientation="left">
                    Dependant Category {index + 1}
                  </Divider>
                  <Form.Item
                    {...restField}
                    name={[name, "MinAge"]}
                    label="Min Age"
                    rules={[
                      { required: true, message: "Please enter Min Age" },
                    ]}
                  >
                    <InputNumber
                      size="large"
                      placeholder="Min Age"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, "MaxAge"]}
                    label="Max Age"
                    rules={[
                      { required: true, message: "Please enter Max Age" },
                    ]}
                  >
                    <InputNumber
                      size="large"
                      placeholder="Max Age"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, "CoverAmount"]}
                    label="Cover Amount"
                    rules={[
                      { required: true, message: "Please enter Cover Amount" },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="Cover Amount"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                  <Form.Item {...tailLayout}>
                    <Button
                      size="large"
                      onClick={() => remove(name)}
                      style={{ marginTop: 8 }}
                      block
                      danger
                    >
                      Remove
                    </Button>
                  </Form.Item>
                </div>
              ))}
              <Form.Item {...tailLayout}>
                {fields.length < 5 && (
                  <Button
                    size="large"
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    Add Family Cover
                  </Button>
                )}
              </Form.Item>
            </>
          )}
        </Form.List>
      )}

      <Form.Item {...tailLayout}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Button
            style={{ marginBottom: 10 }}
            block
            size="large"
            loading={formLoading}
            onClick={() => {
              submit().then((result) => {
                if (result) {
                  gotoStep(current + 1);
                } else if (!result) {
                  gotoStep(current + 2);
                }
              });
            }}
          >
            Submit
          </Button>
        </Space>
      </Form.Item>
    </>,
  ];

  return (
    <Modal
      title="Edit Product"
      open={show}
      onOk={handleModal}
      onCancel={handleModal}
      width={820}
      footer={null}
    >
      {/* Steps Form */}
      <div style={{ display: "flex", gap: 24 }}>
        {current !== 2 && (
          <div style={{ flexShrink: 0, borderRight: "1px solid #f0f0f0", paddingRight: 16, whiteSpace: "nowrap" }}>
            <Steps {...stepsProps} direction="vertical">
              <Step title="Product Info" />
              <Step title="Cover Info" />
            </Steps>
          </div>
        )}

        <div style={{ flex: 1, marginTop: 24 }}>
          <Form {...layout} {...formProps} style={{ maxWidth: 820 }}>
            {formList[current]}
          </Form>

          {current === 2 && (
            <Result status="success" title="Successfully updated.">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button
                  loading={loading}
                  block
                  size="large"
                  onClick={handleModal}
                >
                  Okay
                </Button>
              </Space>
            </Result>
          )}

          {current === 3 && (
            <Result status="error" title={errorMsg}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button block size="large" onClick={handleModal}>
                  Okay
                </Button>
              </Space>
            </Result>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default EditProductModal;
